import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
} from '@mui/material';
import { TrendingUp, Menu, Close } from '@mui/icons-material';

const Header: React.FC = () => {
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { label: '2D Sensitivity Matrix', targetId: 'sensitivity-section' },
    { label: 'Instant Scenarios', targetId: 'scenarios-section' },
    { label: 'Smart File Manager', targetId: 'filemanager-section' },
    { label: 'UK Features', targetId: 'features-section' },
  ];

  const scrollToSection = (e: React.MouseEvent, id: string) => {
    if (window.location.pathname !== '/' && window.location.pathname !== '/index.html') {
      window.location.href = `/#${id}`;
      return;
    }
    e.preventDefault();
    setMobileOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToSignup = (e?: React.MouseEvent) => {
    if (window.location.pathname !== '/' && window.location.pathname !== '/index.html') {
      window.location.href = '/#beta-signup';
      return;
    }
    if (e) e.preventDefault();
    setMobileOpen(false);
    const element = document.getElementById('beta-signup');
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <AppBar
      component="header"
      position="fixed"
      elevation={0}
      sx={{
        backdropFilter: 'blur(12px)',
        backgroundColor: 'rgba(255, 255, 255, 0.92)',
        borderBottom: '1px solid #e2e8f0',
        zIndex: theme.zIndex.drawer + 1,
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ justifyContent: 'space-between', py: 1 }}>
          {/* Logo & Subtitle */}
          <Box
            component="a"
            href="/"
            onClick={(e) => {
              if (window.location.pathname === '/' || window.location.pathname === '/index.html') {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
            aria-label="FiMod Real Estate Home"
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, textDecoration: 'none' }}
          >
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: 'rgba(4, 120, 87, 0.08)',
                border: '1px solid rgba(4, 120, 87, 0.2)',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <TrendingUp sx={{ color: '#047857', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                component="span"
                sx={{
                  fontWeight: 800,
                  color: '#0f172a',
                  fontFamily: '"Space Grotesk", sans-serif',
                  letterSpacing: '-0.02em',
                  display: 'block',
                }}
              >
                FiMod
              </Typography>
              <Typography variant="caption" sx={{ color: '#047857', fontSize: '0.65rem', display: 'block', fontWeight: 700, lineHeight: 1 }}>
                UK REAL ESTATE ENGINE
              </Typography>
            </Box>
          </Box>

          {/* Desktop Nav */}
          <Box component="nav" aria-label="Main Navigation" sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3.5 }}>
            {navLinks.map((link) => (
              <Typography
                component="a"
                href={`/#${link.targetId}`}
                key={link.targetId}
                onClick={(e) => scrollToSection(e, link.targetId)}
                variant="body2"
                sx={{
                  color: '#475569',
                  fontWeight: 600,
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    color: '#047857',
                  },
                }}
              >
                {link.label}
              </Typography>
            ))}
          </Box>

          {/* Desktop Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              component="a"
              href="#beta-signup"
              variant="contained"
              color="primary"
              data-umami-event="HeaderClickSignupNav"
              data-umami-event-source="Header-Desktop"
              onClick={scrollToSignup}
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                fontSize: '0.875rem',
                py: 1,
                px: 2.5,
              }}
            >
              Request Beta Access
            </Button>

            <IconButton
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle Navigation Menu"
              sx={{ display: { xs: 'flex', md: 'none' }, color: '#0f172a' }}
            >
              {mobileOpen ? <Close /> : <Menu />}
            </IconButton>
          </Box>
        </Toolbar>
      </Container>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            bgcolor: '#ffffff',
            borderLeft: '1px solid #e2e8f0',
            p: 3,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 800 }}>
            FiMod Navigation
          </Typography>
          <IconButton onClick={() => setMobileOpen(false)} aria-label="Close menu" sx={{ color: '#475569' }}>
            <Close />
          </IconButton>
        </Box>

        <List>
          {navLinks.map((link) => (
            <ListItem
              button
              component="a"
              href={`/#${link.targetId}`}
              key={link.targetId}
              onClick={(e) => scrollToSection(e, link.targetId)}
              sx={{
                borderRadius: 2,
                mb: 1,
                textDecoration: 'none',
                '&:hover': { bgcolor: '#f1f5f9' },
              }}
            >
              <ListItemText
                primary={link.label}
                primaryTypographyProps={{ fontWeight: 600, color: '#0f172a' }}
              />
            </ListItem>
          ))}
        </List>

        <Box sx={{ mt: 4 }}>
          <Button
            fullWidth
            component="a"
            href="#beta-signup"
            variant="contained"
            color="primary"
            data-umami-event="HeaderClickSignupNav"
            data-umami-event-source="Header-Mobile"
            onClick={scrollToSignup}
          >
            Request Beta Access
          </Button>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Header;
